import { useTranslation } from "react-i18next";

import Input from "../../../../components/Form/Input/input";
import { Select } from "../../../../components/Form/Input/select";
import Label from "../../../../components/Form/Label";
import { FormGroup } from "../../../../components/Form/styles";
import { buildCategory, CATEGORY_SEGMENTS, OTHER_SEGMENT, parseCategory } from "../../../../model/categories";

type Props = {
  id: string;
  /** `site.category`: "segment/specialty", "segment", "other" or a legacy key. */
  value?: string;
  /** `site.categoryOther`: the user's own words when the segment is "other". */
  other?: string;
  onChange: (value: string, other: string) => void;
  /** Marks the segment (and the "other" text) as required. */
  required?: boolean;
  /** For "other", keep the specialty field (disabled) instead of asking for the user's own words. */
  disableOtherText?: boolean;
};

/** Whether the field is filled enough to save: a segment ("other" needs its text), or an untouched legacy key. */
export const isCategoryComplete = (value?: string, other?: string) => {
  const parsed = parseCategory(value);
  if (parsed.kind === "legacy") {
    return true;
  }
  return parsed.kind === "segment" && (parsed.segment !== OTHER_SEGMENT || !!other?.trim());
};

/**
 * The Keepo's field of work in two steps: the segment, then an optional specialty from it ("other"
 * asks for the user's own words instead). A legacy key from before segments stays selected as is.
 */
const CategoriesInputSelect = ({ id, value, other = "", onChange, required, disableOtherText }: Props) => {
  const { t } = useTranslation();
  const parsed = parseCategory(value);
  const segment = parsed.kind === "segment" ? parsed.segment : parsed.kind === "legacy" ? parsed.key : "";
  const specialties = CATEGORY_SEGMENTS.find(({ key }) => key === segment)?.specialties || [];

  return (
    <>
      <FormGroup>
        <Select
          id={id}
          name={id}
          value={segment}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value, "")}>
          {/* Until one is picked: otherwise the first segment would look chosen while none is saved */}
          {parsed.kind === "none" && (
            <option value="" disabled>
              {t("components.categoryInput.segmentPlaceholder")}
            </option>
          )}
          {parsed.kind === "legacy" && <option value={parsed.key}>{t(`site.categories.${parsed.key}`)}</option>}
          {CATEGORY_SEGMENTS.map(({ key }) => (
            <option key={key} value={key}>
              {t(`site.categoryTree.${key}.label`)}
            </option>
          ))}
        </Select>
        <Label htmlFor={id} required={required} text={t("components.categoryInput.segment")} />
      </FormGroup>

      {parsed.kind === "segment" && segment === OTHER_SEGMENT && !disableOtherText && (
        <FormGroup>
          <Input
            type="text"
            id={`${id}-other`}
            maxLength={60}
            required
            value={other}
            placeholder={t("components.categoryInput.otherPlaceholder")}
            onChange={e => onChange(OTHER_SEGMENT, e.target.value)}
            label={t("components.categoryInput.other")}
          />
        </FormGroup>
      )}

      {parsed.kind === "segment" && segment === OTHER_SEGMENT && disableOtherText && (
        <FormGroup>
          <Select id={`${id}-specialty`} name={`${id}-specialty`} value="" disabled>
            <option value="">{t("components.categoryInput.specialtyPlaceholder")}</option>
          </Select>
          <Label htmlFor={`${id}-specialty`} text={t("components.categoryInput.specialty")} />
        </FormGroup>
      )}

      {parsed.kind === "segment" && specialties.length > 0 && (
        <FormGroup>
          <Select
            id={`${id}-specialty`}
            name={`${id}-specialty`}
            value={parsed.specialty}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              onChange(buildCategory(segment, e.target.value), "")
            }>
            <option value="">{t("components.categoryInput.specialtyPlaceholder")}</option>
            {specialties.map(key => (
              <option key={key} value={key}>
                {t(`site.categoryTree.${segment}.specialties.${key}`)}
              </option>
            ))}
          </Select>
          <Label htmlFor={`${id}-specialty`} text={t("components.categoryInput.specialty")} />
        </FormGroup>
      )}
    </>
  );
};

export default CategoriesInputSelect;
